import { Component, OnInit, ViewChild } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { FormControl } from '@angular/forms'
import { Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import { BehaviorSubject, merge, throwError } from 'rxjs'
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators'
import * as lodash from 'lodash'

import * as fromRoot from '@tv3/store/state'
import { createLocaleCompareSort, isEmptyTable } from '@tokeet-frontend/utility'
import { UserStorage } from '@tv3/services/utils/storage.service'
import { TableType } from '@tv3/shared/empty-table/table-type'
import {
  Channel,
  ChannelAdjustment,
  ChannelAdjustmentService,
  ConfirmDialogService,
  Destroyable,
  LoadChannelAdjustmentsComplete,
  Rental,
  selectAllApiChannelViews,
  selectAllRentals,
  selectApiChannelsWithDefaults,
  sortAscend,
  Toaster,
  tokeetDashboardChannel,
  untilDestroy,
} from '@tokeet/tv3-platform'
import { EditChannelAdjustmentDialogService } from '../edit-channel-adjustment-dialog/edit-channel-adjustment-dialog.service'
import * as R from 'ramda'

@Component({
  selector: 'app-channel-adjustments-table',
  templateUrl: './channel-adjustments-table.component.html',
  styleUrls: ['./channel-adjustments-table.component.scss'],
})
export class ChannelAdjustmentsTableComponent extends Destroyable implements OnInit {
  tableType = TableType.ChannelAdjustments

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  channelAdjustments$ = new BehaviorSubject<ChannelAdjustment[]>([])

  channels: Channel[] = []
  rentals: Record<string, Rental> = {}

  channelsControl = new FormControl()
  rentalsControl = new FormControl()

  displayedColumns = ['channelName', 'adjustAmount', 'adjustPercent', 'rentals', 'edit']

  dataSource = new MatTableDataSource<ChannelAdjustment>()
  isEmptyTable$ = isEmptyTable(this.dataSource)

  constructor(
    private store: Store<fromRoot.State>,
    private actions$: Actions,
    private storage: UserStorage,
    private toaster: Toaster,
    private confirmDialog: ConfirmDialogService,
    private channelAdjustmentService: ChannelAdjustmentService,
    private editChannelAdjustmentDialog: EditChannelAdjustmentDialogService
  ) {
    super()
  }

  ngOnInit() {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = createLocaleCompareSort(this.sortDataAccessor)

    this.loadFilters()

    this.store
      .select(selectAllApiChannelViews)
      .pipe(
        map((channels) => sortAscend('friendlyName')(channels) as Channel[]),
        map((channels) => R.concat(channels)([tokeetDashboardChannel])),
        untilDestroy(this)
      )
      .subscribe((channels) => {
        this.channels = channels
      })

    this.store
      .select(selectAllRentals)
      .pipe(
        distinctUntilChanged(),
        tap((rentals) => {
          this.rentals = lodash.keyBy(rentals, (rental) => rental.id)
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.actions$
      .pipe(
        ofType(LoadChannelAdjustmentsComplete.type),
        tap((action: any) => {
          this.channelAdjustments$.next(action.channelAdjustments)
        })
      )
      .subscribe()

    merge(this.channelsControl.valueChanges, this.rentalsControl.valueChanges, this.channelAdjustments$)
      .pipe(
        tap(() => {
          this.saveFilters()

          const channels = lodash.keyBy(this.channelsControl.value as string[])
          const rentals = lodash.keyBy(this.rentalsControl.value as string[])

          const hasChannels = !lodash.isEmpty(channels)
          const hasRentals = !lodash.isEmpty(rentals)

          let channelAdjustments = this.channelAdjustments$.value

          if (hasChannels || hasRentals) {
            channelAdjustments = channelAdjustments.filter((ca) => {
              if (hasChannels && !channels[ca.channelId]) return false

              if (hasRentals) {
                const found = lodash.find(ca.rentalIds, (rentalId) => rentals[rentalId])
                if (!found) return false
              }

              return true
            })
          }

          this.dataSource.data = channelAdjustments
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.loadChannelAdjustments()
  }

  private loadChannelAdjustments() {
    this.channelAdjustmentService
      .all()
      .pipe(
        tap((channelAdjustments: any) => {
          this.updateChannelAdjustments(channelAdjustments)
        })
      )
      .subscribe()
  }

  toList<T = any>(data: Record<string, T>) {
    return lodash.values(data)
  }

  clearFilters() {
    this.storage.remove(this.filtersStorageKey)
    this.loadFilters()
  }

  sortDataAccessor = (adjustment: ChannelAdjustment, property: string) => {
    switch (property) {
      case 'channelName':
        return this.getChannelName(adjustment.channelId)
      case 'rentals':
        return adjustment.rentalIds ? adjustment.rentalIds.length : 0
      default:
        return adjustment[property]
    }
  }

  getRentalNames = (rentalIds: string[]) => {
    return rentalIds
      .map((rentalId) => this.rentals[rentalId] && this.rentals[rentalId].name)
      .filter((name) => name)
      .join(', ')
  }

  getChannelName = (channelId: string) => {
    const channel = this.channels.find((c) => c.id === channelId)

    return channel
      ? channel.friendlyName || channel.name
      : channelId === 'tokeet' || channelId === tokeetDashboardChannel.id
      ? 'Tokeet Dashboard'
      : 'Unknown'
  }

  getRentalsTooltip = (rentalIds: string) => {
    const rentals = lodash.keyBy(this.rentals, (rental) => rental.id)

    const rentalNames = lodash.map(rentalIds, (rentalId) => {
      const rental = rentals[rentalId]

      return rental && rental.name
    })

    return lodash.compact(rentalNames).join(', ')
  }

  onAdd() {
    this.editChannelAdjustmentDialog.open()
  }

  onEdit(channelAdjustment: ChannelAdjustment) {
    this.editChannelAdjustmentDialog.open({ channelAdjustment })
  }

  onDelete(channelAdjustment: ChannelAdjustment) {
    this.confirmDialog.confirm().subscribe(() => {
      this.deleteChannelAdjustment(channelAdjustment)
    })
  }

  deleteChannelAdjustment(channelAdjustment: ChannelAdjustment) {
    this.channelAdjustmentService
      .deleteChannelAdjustment(channelAdjustment.id)
      .pipe(
        tap(() => {
          const channelAdjustments = this.dataSource.data.filter((ca) => ca.id !== channelAdjustment.id)

          this.toaster.success('Channel adjustment deleted successfully.')
          this.updateChannelAdjustments(channelAdjustments)
        }),
        catchError((error) => {
          this.toaster.error('Unable to delete channel adjustment.')
          return throwError(error)
        })
      )
      .subscribe()
  }

  updateChannelAdjustments(channelAdjustments: ChannelAdjustment[]) {
    this.store.dispatch(LoadChannelAdjustmentsComplete({ channelAdjustments }))
  }

  private saveFilters() {
    const channels = this.channelsControl.value
    const rentals = this.rentalsControl.value

    this.storage.set(this.filtersStorageKey, {
      channels,
      rentals,
    })
  }

  private loadFilters() {
    const data = this.storage.get(this.filtersStorageKey) || {}

    this.channelsControl.setValue(data.channels || [])
    this.rentalsControl.setValue(data.rentals || [])
  }

  private get filtersStorageKey() {
    return 'CHANNEL_ADJUSTMENTS_TABLE_FILTERS'
  }
}
